import { debugLog } from '../utils/debugLog';
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { memoryStore, enableCloudSync, disableCloudSync, syncFromCloud, retrySync, clearLocalData, ensureLocalDataForUser } from '../utils/storage';
import { registerDevice, stopHeartbeat } from '../utils/deviceManager';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storageMode, setStorageModeState] = useState('cloud');
  const [syncStatus, setSyncStatus] = useState('idle');
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [syncVersion, setSyncVersion] = useState(0); // increments after each successful cloud pull
  const [initialSyncDone, setInitialSyncDone] = useState(false);
  const [supabaseUnreachable, setSupabaseUnreachable] = useState(false);
  const cloudInitDone = useRef(false);
  const currentUserIdRef = useRef(null);
  const [deviceBlock, setDeviceBlock] = useState(null);

  const loadProfile = useCallback(async (userId, userEmail, userMeta) => {
    if (!supabase) return;
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error && error.code === 'PGRST116') {
      // No profile yet — first login. Create with 30-day trial.
      const trialEnd = new Date();
      trialEnd.setDate(trialEnd.getDate() + 30);

      const { data: newProfile, error: insertError } = await supabase
        .from('profiles')
        .insert({
          id:                  userId,
          email:               userEmail,
          full_name:           userMeta?.full_name || '',
          avatar_url:          userMeta?.avatar_url || '',
          trial_ends_at:       trialEnd.toISOString(),
          subscription_status: 'trial',
        })
        .select()
        .single();

      if (insertError && insertError.code === '23505') {
        // Race condition: profile was just inserted by another request — re-fetch
        const { data: refetched } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();
        setProfile(refetched);
      } else {
        setProfile(newProfile);
      }
    } else {
      setProfile(data);
    }
  }, []);

  const initCloudSync = useCallback(async (prof) => {
    if (cloudInitDone.current) return;
    cloudInitDone.current = true;
    const mode = prof?.storage_mode || 'cloud';
    setStorageModeState(mode);

    memoryStore._onSyncChange = () => {
      setSyncStatus(memoryStore._syncStatus);
      setLastSyncTime(memoryStore._lastSync);
      setSyncError(memoryStore._lastError);
    };

    if (mode === 'cloud') {
      // Guard: clear IndexedDB if it still holds another user's data
      // (happens when the app starts with stale local data before auth resolves)
      await ensureLocalDataForUser(prof.id);
      memoryStore._cloudEnabled = true;
      // Unblock the UI immediately with local IndexedDB data — sync runs in background.
      // The pension-data-synced event (dispatched by syncFromCloud) will refresh hooks.
      setInitialSyncDone(true);
      syncFromCloud()
        .then(() => setSyncVersion(v => v + 1))
        .catch(() => {}); // syncFromCloud already calls memoryStore._setSyncError internally
    } else {
      // Local-only mode — nothing to sync
      setInitialSyncDone(true);
    }
  }, []);

  const setStorageMode = useCallback(async (mode) => {
    if (!session?.user || !supabase) return;
    setStorageModeState(mode);
    await supabase.from('profiles').update({ storage_mode: mode }).eq('id', session.user.id);
    setProfile(prev => prev ? { ...prev, storage_mode: mode } : prev);
    if (mode === 'cloud') {
      await enableCloudSync();
    } else {
      await disableCloudSync(false);
    }
  }, [session]);

  const checkDevice = useCallback(async () => {
    const result = await registerDevice();
    if (!result.allowed) {
      setDeviceBlock(result);
    } else {
      setDeviceBlock(null);
    }
    return result.allowed;
  }, []);

  useEffect(() => {
    if (profile) {
      checkDevice()
      .then(allowed => {
        if (allowed) initCloudSync(profile);
        else setInitialSyncDone(true); // device blocked — unblock UI so DeviceBlockedModal shows
      })
      .catch(err => {
        console.warn('[Auth] Device check failed — proceeding with cloud sync anyway:', err);
        initCloudSync(profile);
      });
    }
    return () => stopHeartbeat();
  }, [profile, initCloudSync, checkDevice]);

  // Safety net: if initialSyncDone is still false after 10s, unblock the UI
  useEffect(() => {
    if (!initialSyncDone && session) {
      const t = setTimeout(() => {
        console.warn('[Auth] initialSyncDone timeout — unblocking UI');
        setInitialSyncDone(true);
      }, 10_000);
      return () => clearTimeout(t);
    }
  }, [initialSyncDone, session]);

  useEffect(() => {
    if (!supabase) {
      console.warn('[Auth] Supabase not configured — running in offline/local mode');
      setLoading(false);
      return;
    }

    debugLog('[Auth] calling getSession...');
    // Backstop only — the fetch timeout in lib/supabase.js now makes getSession
    // reject within ~8s instead of hanging. This guards the rare case where the
    // promise never settles at all.
    const timeout = setTimeout(() => {
      console.warn('[Auth] getSession timeout — releasing loading');
      setSupabaseUnreachable(true);
      setLoading(false);
    }, 30000);

    supabase.auth.getSession().then(({ data: { session } }) => {
      clearTimeout(timeout);
      debugLog('[Auth] getSession result:', session ? 'has session' : 'no session');
      setSupabaseUnreachable(false);
      setSession(session);
      if (session?.user) {
        loadProfile(
          session.user.id,
          session.user.email,
          session.user.user_metadata
        ).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    }).catch((err) => {
      clearTimeout(timeout);
      console.error('[Auth] getSession error:', err);
      // AbortError = our fetch timeout fired = Supabase unreachable.
      if (err?.name === 'AbortError') setSupabaseUnreachable(true);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        // IMPORTANT: keep this callback synchronous and never `await` Supabase
        // calls inside it. supabase-js runs the callback while holding the auth
        // lock; awaiting loadProfile() (a PostgREST query) re-enters the same
        // lock and deadlocks the concurrent getSession() on first load — which is
        // exactly what caused the "getSession timeout" hang that cleared on F5.
        // Update React state synchronously, then defer all Supabase/IndexedDB
        // work to a macrotask so the lock is released first.
        const prevUserId = currentUserIdRef.current;
        const newUserId  = newSession?.user?.id ?? null;
        currentUserIdRef.current = newUserId;

        if (newSession) setSupabaseUnreachable(false);
        setSession(newSession);

        setTimeout(async () => {
          // Clear local data when signing out OR when a different user signs in
          if (!newSession || (prevUserId && newUserId !== prevUserId)) {
            debugLog('[Auth] user changed — clearing local data');
            await clearLocalData();
            cloudInitDone.current = false;
            setDeviceBlock(null);
          }
          if (newSession?.user) {
            await loadProfile(
              newSession.user.id,
              newSession.user.email,
              newSession.user.user_metadata
            );
          } else {
            setProfile(null);
          }
        }, 0);
      }
    );

    return () => subscription.unsubscribe();
  }, [loadProfile]);

  const signInWithGoogle = () => {
    if (!supabase) return Promise.reject(new Error('Supabase not configured'));
    return supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        queryParams: { prompt: 'select_account' },
      },
    });
  };

  const signOut = async () => {
    if (!supabase) return;
    await clearLocalData();
    cloudInitDone.current = false;
    currentUserIdRef.current = null;
    setInitialSyncDone(false);

    // scope:'local' — מבטל רק את ה-session של המכשיר הנוכחי. ברירת המחדל של
    // supabase-js היא scope:'global', שמבטלת את ה-sessions של *כל* המכשירים:
    // מכשיר אחר שנשאר מחובר המשיך לרוץ עם session מבוטל — /auth/v1/user החזיר
    // 403 שוב ושוב, והסנכרון נכשל בשקט עד שהעלאות קבצים נעלמו.
    //
    // אם קריאת הרשת נכשלת (רשת איטית / Supabase Free Tier מתעורר), signOut
    // מחזיר { error } בלי לנקות את localStorage ובלי SIGNED_OUT — לכן ניסיון
    // נוסף, ובכל מקרה איפוס ה-state של React בהמשך מוריד ל-LoginPage.
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) {
      console.warn('[Auth] signOut failed, retrying local session cleanup:', error.message);
      await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    }

    // Safety net: force React state to signed-out even if onAuthStateChange didn't fire.
    setSession(null);
    setProfile(null);
  };

  const isExpired = (() => {
    if (!profile) return false;
    if (profile.subscription_status === 'active') return false;
    if (profile.subscription_status === 'cancelled' || profile.subscription_status === 'expired') return true;
    if (profile.subscription_status === 'trial') {
      return profile.trial_ends_at ? new Date(profile.trial_ends_at) < new Date() : false;
    }
    return false;
  })();

  // After removing a blocked device, re-check the device AND trigger cloud sync
  // if now allowed. checkDevice alone won't call initCloudSync (that's only done
  // in the profile useEffect on initial load).
  const retryDevice = useCallback(async () => {
    const allowed = await checkDevice();
    if (allowed) await initCloudSync(profile);
  }, [checkDevice, initCloudSync, profile]);

  const adminEmail = (typeof window !== 'undefined' && window.__ENV__?.REACT_APP_ADMIN_EMAIL)
    || process.env.REACT_APP_ADMIN_EMAIL || '';
  const isAdmin = !!(adminEmail && session?.user?.email === adminEmail);

  // Feature flag: גישה לאחזקות — true אם אדמין או אם הופעל ידנית ע"י אדמין
  const hasHoldingsFeature = isAdmin || profile?.has_holdings === true;

  return (
    <AuthContext.Provider value={{
      session,
      user:    session?.user ?? null,
      profile,
      loading,
      supabaseUnreachable,
      isExpired,
      isAdmin,
      hasHoldingsFeature,
      signInWithGoogle,
      signOut,
      storageMode,
      setStorageMode,
      syncStatus,
      lastSyncTime,
      syncError,
      syncVersion,
      initialSyncDone,
      retrySync,
      deviceBlock,
      retryDevice,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
